// ** React Imports
import React, { memo, ReactNode, useCallback, useEffect, useRef } from 'react';

// ** Third Party Imports
import { toaster } from '@saleshandy/design-system';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/root-reducer';

// ** Types Imports
import { ExceptionTypes } from '../../../../shared/types';

// ** Custom Imports
import {
  executeOnRequestStatus,
  redirectWithoutRefresh,
} from '../../../../shared/utils';
import { getSequenceRequest, saveAiSeqGeneratorStepDetailsRequest } from '../../extra-actions';
import {
  addAiSeqCopilotUserChat,
  resetAiSeqCopilotUserChat,
  resetGenerateAiSeqModal,
  resetSaveAiSeqGeneratorStepDetailsRequest,
  resetSequence,
  setGenerateAiSeqModalCurrentStepPayload,
  setGenerateAiSeqModalIsOpen,
} from '../../sequence-slice';
import AiSeqGeneratorModal from './ai-seq-generator-modal';
import { handleAiSeqChatFlow } from './components/ai-seq-steps-list/util/helper';
import { AiSequenceGeneratorSteps } from './enums';

/**
 * @description Container for the AI Sequence Generator Modal
 * @returns => React Container Component
 */
const AiSeqGeneratorModalContainer: React.FC = () => {
  // ** Refs
  const shownStepsRef = useRef<Set<string>>(new Set());
  const aiSeqCancelTokenRef = useRef<any>(null);

  // ** Redux
  const dispatch = useDispatch();
  const { status } = useSelector(
    (state: RootState) => state?.sequence?.saveAiSeqGeneratorStepDetailsRequest,
  );
  const hasStepSkipped = useSelector(
    (state: RootState) => state.sequence.hasAiSeqStepSkipped,
  );
  const aiSeqChatMessage = useSelector(
    (state: RootState) => state?.sequence?.aiSeqChatMessage,
  );

  const {
    isOpen,
    currentStep,
    sessionId,
    data,
    id,
    regeneratedStep,
    currentStepPayload,
  } = useSelector((state: RootState) => state?.sequence?.generateAiSeqModal);

  const { aiSeqCopilotUserChat } = useSelector(
    (state: RootState) => state.sequence,
  );

  const firstName = useSelector(
    (state: RootState) => state?.myProfile?.myProfile?.firstName,
  );

  // ** Handlers
  const handleModalClose = useCallback(() => {
    dispatch(resetAiSeqCopilotUserChat());
    dispatch(setGenerateAiSeqModalIsOpen(false));
    dispatch(resetGenerateAiSeqModal());
    dispatch(resetSaveAiSeqGeneratorStepDetailsRequest());
  }, [dispatch]);

  const addChatMessage = useCallback(
    (message: ReactNode | string, isUser = false) => {
      dispatch(
        addAiSeqCopilotUserChat({
          id: aiSeqCopilotUserChat?.length + 1,
          message,
          speaker: isUser ? 'user' : 'saleshandy-copilot',
        }),
      );
    },
    [dispatch, aiSeqCopilotUserChat],
  );

  useEffect(() => {
    if (id) {
      executeOnRequestStatus({
        status,
        onSuccess: () => {
          const pathValue = window?.location?.pathname?.split('/');
          if(pathValue?.[1] === 'sequence' && Number(pathValue?.[2])){
            dispatch(resetSequence())
            dispatch(getSequenceRequest(Number(id)))
          }
          redirectWithoutRefresh(`/sequence/${id}/steps`);
          handleModalClose();
          toaster.success('AI Sequence Generated Successfully');
        },
      });
    }
  }, [status]);

  const discardSession = useCallback(() => {
    if (aiSeqCancelTokenRef?.current) {
      aiSeqCancelTokenRef?.current?.cancel({
        type: ExceptionTypes.Silent,
        message: 'AI_SEQ_GEN_MODAL_CLOSED',
      });
      aiSeqCancelTokenRef.current = null;
    }

    shownStepsRef.current = new Set();
    dispatch(
      saveAiSeqGeneratorStepDetailsRequest({
        sessionId,
        stepName: currentStep,
        hasQuit: true,
      }),
    );
    dispatch(resetSaveAiSeqGeneratorStepDetailsRequest());
  }, [dispatch, sessionId, currentStep, aiSeqCancelTokenRef?.current]);

  // ** Save Current Step Details Implement this
  const saveCurrentStepDetails = useCallback(
    (payload: any) => {
      // Create a new cancel token for the API call
      aiSeqCancelTokenRef.current = axios?.CancelToken?.source();
      const cancelToken = aiSeqCancelTokenRef?.current?.token || null;
      dispatch(setGenerateAiSeqModalCurrentStepPayload(payload));
      dispatch(
        saveAiSeqGeneratorStepDetailsRequest({
          ...(sessionId ? { sessionId } : {}),
          stepName: currentStep,
          data: { ...payload },
          ...(cancelToken ? { cancelToken } : {}),
        }),
      );
    },
    [dispatch, sessionId, currentStep, aiSeqCancelTokenRef?.current],
  );

  const handleAiSeqChatFlowWrapper = useCallback(
    (
      stepName: AiSequenceGeneratorSteps,
      chatFlowStatus: 'success' | 'pending',
    ) => {
      handleAiSeqChatFlow(
        stepName,
        chatFlowStatus,
        data,
        aiSeqChatMessage,
        firstName,
        addChatMessage,
        shownStepsRef.current,
        hasStepSkipped,
      );
    },
    [
      data,
      aiSeqChatMessage,
      firstName,
      addChatMessage,
      hasStepSkipped,
      handleAiSeqChatFlow,
      shownStepsRef?.current,
    ],
  );

  // ** Effects
  useEffect(() => {
    if (isOpen) {
      shownStepsRef.current = new Set();
    }
  }, [isOpen]);

  // ** Copilot Chat display as per status
  useEffect(() => {
    if (isOpen && currentStep && status) {
      executeOnRequestStatus({
        status,
        onSuccess: () => handleAiSeqChatFlowWrapper(currentStep, 'success'),
        onPending: () => handleAiSeqChatFlowWrapper(currentStep, 'pending'),
      });
    }
  }, [status, currentStep, handleAiSeqChatFlowWrapper]);

  return (
    <AiSeqGeneratorModal
      isOpen={isOpen}
      aiSeqGeneratorStatus={status}
      currentStep={currentStep}
      chatConversation={aiSeqCopilotUserChat}
      handleModalClose={handleModalClose}
      addChatMessage={addChatMessage}
      discardSession={discardSession}
      saveAiSeqGeneratorStepDetailsRequestStatus={status}
      saveCurrentStepDetails={saveCurrentStepDetails}
      currentStepPayload={currentStepPayload}
      regeneratedStep={regeneratedStep}
    />
  );
};

export default memo(AiSeqGeneratorModalContainer);
